import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
    TableRow, 
    TableCell, 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
    IconButton,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch } from 'react-redux';
import { heuresActions } from '../../../../modules/heures/heures.actions';
import FeuilleDeTempsDataField from '../form/FeuilleDeTempsDataField';
import { Formik } from 'formik';
import { object, date, number } from 'yup';
import { format, parseISO } from 'date-fns';
import Constants from '../../../../utils/constants';

/**
 * Validation schema for the form
 * Validates that:
 * - operateur is required
 * - heureDebut is required
 * - heureFin is required and after heureDebut
 * - heureFin is not more than 24 hours after heureDebut
 * - tempsPauseMinutes is required
 */
const fdtValidationSchema = object({
    operateur: number().required('Veuillez sélectionner un opérateur'),
    heureDebut: date()
        .required("L'heure de début est requise"),
    heureFin: date()
        .required("L'heure de fin est requise")
        .test(
            'is-after-start',
            "L'heure de fin doit être postérieure à l'heure de début",
            function (value) {
                const {heureDebut} = this.parent;
                return value > heureDebut;
            }
        )
        .test(
            'within-24h',
            "L'heure de fin ne doit pas être plus de 24 heures après l'heure de début",
            function (value) {
                const { heureDebut } = this.parent;
                if (!heureDebut || !value) return true;
                const maxEnd = new Date(heureDebut.getTime() + 24 * 60 * 60 * 1000); // heure début + 24h
                return value <= maxEnd;
            }
        ),
    tempsPauseMinutes: number().required("Le temps pause est requis"),
});

/**
 * Component for displaying and editing a single day's data in the weekly time sheet.
 * 
 * @param {Object} jour - The day's data
 * @param {string|number} operateurId - The ID of the operator
 * @param {string} operateurLibelle - The display name of the operator
 * @param {string|number} periodeId - The ID of the pay period
 * @returns {JSX.Element} - The rendered component
 */
function FeuillesTempsHebdoDayCell({ jour, operateurId, operateurLibelle, periodeId }) {
    const [open, setOpen] = useState(false);
    const dispatch = useDispatch();

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    /**
     * Get the background and text color style based on the statutJournee value
     * 
     * @param {string} statutJournee - The status of the day (WD, AM, CP, RTT, JF)
     * @returns {Object} - The style object with backgroundColor and color properties
     */
    const getStatutJourneeStyle = (statutJournee) => {
        const statut = Constants.RH_STATUT_JOURNEE_DATA.find(s => s.id === statutJournee);
        if (statut && statut.id !== 'WD') {
            return {
                backgroundColor: statut.color,
                color: statut.fontColor
            };
        }
        return {}; // Default empty style if no match
    };

    /**
     * Format pause minutes to a human-readable format
     * 
     * @param {number} minutes - The pause duration in minutes
     * @returns {string} - The formatted pause duration (e.g., "30 minutes", "1h 30min")
     */
    const formatPauseMinutes = (minutes) => {
        if (!minutes) return "N/A";
        if (minutes < 60) {
            return `${minutes} minute${minutes > 1 ? 's' : ''}`;
        } else {
            const heures = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return `${heures}h${mins > 0 ? ` ${mins}min` : ''}`;
        }
    };

    /**
     * Handle form submission for updating or creating a feuille de temps
     * 
     * @param {Object} values - The form values
     * @param {Object} formikBag - The Formik bag object containing setSubmitting
     * @param {Function} formikBag.setSubmitting - Function to set the submitting state
     */
    const handleSubmit = (values, { setSubmitting }) => {
        if (jour.exists) {
            // Update existing entry
            dispatch(heuresActions.updateFeuilleDeTemps(jour.feuilleDeTempsId, values))
                .then(() => {
                    setOpen(false);
                    // Refresh data
                    dispatch(heuresActions.fetchFeuillesTempsHebdo(periodeId, operateurId));
                })
                .catch(error => {
                    console.error("Error updating entry:", error);
                })
                .finally(() => {
                    setSubmitting(false);
                });
        } else {
            // Create new entry
            dispatch(heuresActions.createEmptyFeuilleDeTemps(
                operateurId,
                jour.dateISO,
                values.statutJournee,
                values.heureDebut,
                values.heureFin,
                values.tempsPauseMinutes
            ))
                .then(() => {
                    setOpen(false);
                    // Refresh data
                    dispatch(heuresActions.fetchFeuillesTempsHebdo(periodeId, operateurId));
                })
                .catch(error => {
                    console.error("Error creating entry:", error);
                })
                .finally(() => {
                    setSubmitting(false);
                });
        }
    };

    /**
     * Prepare initial values for the form based on whether we're editing an existing entry or creating a new one
     * 
     * @returns {Object} - The initial form values
     */
    const getInitialValues = () => {
        if (jour.exists) {
            return {
                operateur: operateurId,
                heureDebut: jour.heureDebut ? format(new Date(`${jour.dateISO}T${jour.heureDebut}`), "yyyy-MM-dd HH:mm:ss") : format(new Date().setHours(9, 0, 0, 0), "yyyy-MM-dd HH:mm:ss"),
                heureFin: jour.heureFin ? format(new Date(`${jour.dateISO}T${jour.heureFin}`), "yyyy-MM-dd HH:mm:ss") : format(new Date().setHours(18, 0, 0, 0), "yyyy-MM-dd HH:mm:ss"),
                tempsPauseMinutes: jour.pauseMinutes || 30,
                statutJournee: "WD" // Default to workday
            };
        } else {
            return {
                operateur: operateurId,
                heureDebut: format(new Date().setHours(9, 0, 0, 0), "yyyy-MM-dd HH:mm:ss"),
                heureFin: format(new Date().setHours(18, 0, 0, 0), "yyyy-MM-dd HH:mm:ss"),
                tempsPauseMinutes: 30,
                statutJournee: "WD" // Default to workday
            };
        }
    };

    return (
        <>
            <TableRow 
                hover 
                style={jour.statutJournee ? getStatutJourneeStyle(jour.statutJournee) : {}}
            >
                <TableCell>{jour.jour}</TableCell>
                <TableCell>{jour.date}</TableCell>
                <TableCell>{jour.heureDebut || "-"}</TableCell>
                <TableCell>{jour.heureFin || "-"}</TableCell>
                <TableCell>{formatPauseMinutes(jour.pauseMinutes)}</TableCell>
                <TableCell>{jour.equipier || "-"}</TableCell>
                <TableCell>{jour.laveuse || "-"}</TableCell>
                <TableCell>{jour.tournee || "-"}</TableCell>
                <TableCell>{jour.client || "-"}</TableCell>
                <TableCell>
                    {jour.exists ? (
                        <Tooltip title="Modifier">
                            <IconButton size="small" onClick={handleOpen}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Créer">
                            <IconButton size="small" onClick={handleOpen}>
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </TableCell>
            </TableRow>

            {/* Dialog for editing or creating entries */}
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {jour.exists ? "Modifier l'entrée" : "Créer une nouvelle entrée"}
                </DialogTitle>
                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={fdtValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <DialogContent>
                                <FeuilleDeTempsDataField
                                    operateurs={[{ id: operateurId, libelle: operateurLibelle }]}
                                    values={values}
                                    errors={errors}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleClose}>Annuler</Button>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    color="primary"
                                    disabled={isSubmitting}
                                >
                                    {jour.exists ? "Mettre à jour" : "Créer"}
                                </Button>
                            </DialogActions>
                        </form>
                    )}
                </Formik>
            </Dialog>
        </>
    );
}

// PropTypes for type checking
FeuillesTempsHebdoDayCell.propTypes = {
    jour: PropTypes.object.isRequired,
    operateurId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    operateurLibelle: PropTypes.string,
    periodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default FeuillesTempsHebdoDayCell;
